/**
 * What the Settings page shows (W2-13 settings-keymap; DESIGN.md §4.20, §5.15): its sections, the built-in rows
 * for every user setting of `UserSettings`, the blocks (keymap editor, skins, checker rules, storage, about) and the
 * rows contributed by feature modules (`FeatureModule.settings`, including `ext.<feature>.*` preferences).
 *
 * A row edits one setting, or one field of an object setting, with a default editor (switch, number, slider,
 * choice, text) or a custom component. Module rows for a path that also has built-in rows replace them (a feature
 * may supply a richer editor). `ext.*` rows go to "Panels and tools". Search runs over the translated title,
 * description and keywords of every item.
 */

import type { ComponentType } from 'react';
import type { AppServices, IconName, SettingDef } from '../../app/registry/types';
import type { SettingPath } from '../../app/settings/types';
import type { Json } from '../../core/util/types';
import type { MessageKey } from '../../i18n/types';
import type { ExperienceLevel } from '../../state/types';
import { matchesTerms } from './keymapModel';
import { m } from './settings.messages';
import { pageSectionOfPath } from './settingsValue';

export type PageSectionId =
  'general' | 'editing' | 'audio' | 'preview' | 'keyboard' | 'checker' | 'storage' | 'features' | 'about';

export interface PageSection {
  readonly id: PageSectionId;
  readonly title: MessageKey;
  readonly description: MessageKey;
  readonly icon: IconName;
}

/** Sections in page order (DESIGN.md §4.20). "Panels and tools" appears only when modules contribute preferences. */
export const PAGE_SECTIONS: readonly PageSection[] = [
  { id: 'general', title: m.sectionGeneral, description: m.sectionGeneralDesc, icon: 'SlidersHorizontal' },
  { id: 'editing', title: m.sectionEditing, description: m.sectionEditingDesc, icon: 'PenLine' },
  { id: 'audio', title: m.sectionAudio, description: m.sectionAudioDesc, icon: 'Volume2' },
  { id: 'preview', title: m.sectionPreview, description: m.sectionPreviewDesc, icon: 'MonitorPlay' },
  { id: 'keyboard', title: m.sectionKeyboard, description: m.sectionKeyboardDesc, icon: 'Keyboard' },
  { id: 'checker', title: m.sectionChecker, description: m.sectionCheckerDesc, icon: 'ListChecks' },
  { id: 'storage', title: m.sectionStorage, description: m.sectionStorageDesc, icon: 'HardDrive' },
  { id: 'features', title: m.sectionFeatures, description: m.sectionFeaturesDesc, icon: 'Blocks' },
  { id: 'about', title: m.sectionAbout, description: m.sectionAboutDesc, icon: 'Info' },
];

export function isPageSectionId(id: string): id is PageSectionId {
  return PAGE_SECTIONS.some((s) => s.id === id);
}

/**
 * An option label: a message, a message with parameters (a value with its unit, "1.5x"), or fixed text (language
 * names in their own language, snap fractions).
 */
export type OptionLabel =
  | MessageKey
  | { readonly raw: string }
  | { readonly key: MessageKey; readonly params: Readonly<Record<string, string | number>> };

export interface ChoiceOption {
  readonly value: Json;
  readonly label: OptionLabel;
  readonly description?: MessageKey;
}

export type RowEditor =
  | { readonly kind: 'boolean' }
  | {
      readonly kind: 'number';
      readonly min: number;
      readonly max: number;
      readonly step?: number;
      readonly precision?: number;
      /** Unit shown after the value: a message, or a module's own text. */
      readonly unit?: OptionLabel;
      /** Displayed value = stored value x scale (seconds shown as ms: 1000). */
      readonly scale?: number;
      /** Nullable number: a switch turns it off (null) or on (`onValue`). */
      readonly nullable?: { readonly offLabel: MessageKey; readonly onLabel: MessageKey; readonly onValue: number };
      readonly integer?: boolean;
    }
  | {
      readonly kind: 'slider';
      readonly min: number;
      readonly max: number;
      readonly step: number;
      /** Logarithmic travel (scroll sensitivity spans 0.05 to 20). */
      readonly log?: boolean;
      readonly format: 'percent' | 'times' | 'plain';
    }
  | { readonly kind: 'choice'; readonly options: readonly ChoiceOption[]; readonly style?: 'segmented' | 'select' }
  | { readonly kind: 'text'; readonly maxLength?: number; readonly placeholder?: MessageKey }
  | { readonly kind: 'custom'; readonly component: ComponentType<{ readonly services: AppServices }> };

export interface RowItem {
  readonly kind: 'row';
  /** Anchor id: the path, plus the field for field rows (`audio.volumes.master`). */
  readonly id: string;
  readonly section: PageSectionId;
  readonly path: SettingPath;
  readonly field: readonly string[];
  /** Sub-heading the row sits under inside its section (consecutive rows with the same group share it). */
  readonly group?: MessageKey;
  readonly title: MessageKey;
  readonly description?: MessageKey;
  /** Extra search words (translated message, space separated). */
  readonly keywords?: MessageKey;
  readonly level: ExperienceLevel;
  readonly editor: RowEditor;
  /** Contributed by a feature module (id), or built in (null). */
  readonly moduleId: string | null;
  /** Replaces the default write (e.g. the keyboard preset goes through the keymap service). */
  readonly write?: (services: AppServices, value: unknown) => void;
  /** Runs after a write (e.g. the language also switches the i18n runtime at once). */
  readonly afterWrite?: (services: AppServices, value: unknown) => void;
  /** A button or note shown after the editor (e.g. "Calibrate"). */
  readonly extra?: ComponentType<{ readonly services: AppServices }>;
}

export type BlockId = 'keymap' | 'skins' | 'checkerRules' | 'storage' | 'about';

export interface BlockItem {
  readonly kind: 'block';
  readonly id: BlockId;
  readonly section: PageSectionId;
  readonly title: MessageKey;
  readonly keywords: MessageKey;
  readonly level: ExperienceLevel;
}

export type PageItem = RowItem | BlockItem;

// ---------------------------------------------------------------------------------------------------------------
// Built-in rows
// ---------------------------------------------------------------------------------------------------------------

interface RowInit extends Omit<RowItem, 'kind' | 'id' | 'field' | 'moduleId' | 'level'> {
  readonly field?: readonly string[];
  readonly level?: ExperienceLevel;
}

function row(init: RowInit): RowItem {
  const field = init.field ?? [];
  return {
    ...init,
    kind: 'row',
    id: field.length === 0 ? init.path : `${init.path}.${field.join('.')}`,
    field,
    level: init.level ?? 'guided',
    moduleId: null,
  };
}

const bool: RowEditor = { kind: 'boolean' };
const choice = (options: readonly ChoiceOption[], style?: 'segmented' | 'select'): RowEditor =>
  style === undefined ? { kind: 'choice', options } : { kind: 'choice', options, style };
const ms: RowEditor = { kind: 'number', min: -1000, max: 1000, step: 1, precision: 0, unit: m.unitMs, integer: true };
const volume: RowEditor = { kind: 'slider', min: 0, max: 1, step: 0.01, format: 'percent' };

/** Hooks the page fills with the components that need services (calibrate button, tour button). */
export interface BuiltinRowHooks {
  readonly calibrate: ComponentType<{ readonly services: AppServices }>;
  readonly tour: ComponentType<{ readonly services: AppServices }>;
  readonly onLocale: (services: AppServices, value: unknown) => void;
  readonly writePreset: (services: AppServices, value: unknown) => void;
  readonly writeAltFree: (services: AppServices, value: unknown) => void;
}

const SNAP_DIVS = [1, 2, 3, 4, 6, 8, 12, 16, 24, 32, 48, 64] as const;

/** Every setting of `UserSettings` the page edits, in page order. */
export function builtinRows(h: BuiltinRowHooks): RowItem[] {
  return [
    // ---------------- General ----------------
    row({
      section: 'general',
      path: 'general.locale',
      title: m.locale,
      description: m.localeDesc,
      keywords: m.localeKeywords,
      editor: choice([
        { value: 'en', label: { raw: 'English' } },
        { value: 'zh-CN', label: { raw: '简体中文' } },
      ]),
      afterWrite: h.onLocale,
    }),
    row({
      section: 'general',
      path: 'general.level',
      title: m.level,
      description: m.levelDesc,
      keywords: m.levelKeywords,
      editor: choice([
        { value: 'guided', label: m.levelGuided, description: m.levelGuidedDesc },
        { value: 'full', label: m.levelFull, description: m.levelFullDesc },
      ]),
    }),
    row({
      section: 'general',
      path: 'general.theme',
      title: m.theme,
      keywords: m.themeKeywords,
      editor: choice([
        { value: 'dark', label: m.themeDark },
        { value: 'light', label: m.themeLight },
        { value: 'highContrast', label: m.themeHighContrast },
      ]),
    }),
    row({
      section: 'general',
      path: 'general.reducedMotion',
      title: m.reducedMotion,
      description: m.reducedMotionDesc,
      keywords: m.reducedMotionKeywords,
      editor: choice([
        { value: 'system', label: m.reducedMotionSystem },
        { value: 'on', label: m.reducedMotionOn },
        { value: 'off', label: m.reducedMotionOff },
      ]),
    }),
    row({
      section: 'general',
      path: 'general.uiScale',
      title: m.uiScale,
      description: m.uiScaleDesc,
      keywords: m.uiScaleKeywords,
      editor: { kind: 'slider', min: 0.75, max: 2, step: 0.05, format: 'percent' },
    }),
    row({
      section: 'general',
      path: 'general.hintBar',
      title: m.hintBar,
      description: m.hintBarDesc,
      editor: choice([
        { value: 'sentences', label: m.hintBarSentences },
        { value: 'keys', label: m.hintBarKeys },
        { value: 'off', label: m.hintBarOff },
      ]),
    }),
    row({
      section: 'general',
      path: 'general.firstUseTips',
      title: m.firstUseTips,
      description: m.firstUseTipsDesc,
      editor: bool,
    }),
    row({
      section: 'general',
      path: 'editing.layout',
      title: m.layout,
      description: m.layoutDesc,
      keywords: m.layoutKeywords,
      editor: choice(
        [
          { value: 'compose', label: m.layoutCompose },
          { value: 'animate', label: m.layoutAnimate },
          { value: 'review', label: m.layoutReview },
          { value: 'laptop', label: m.layoutLaptop },
        ],
        'select',
      ),
    }),
    row({
      section: 'general',
      path: 'general.tourDone',
      title: m.tour,
      description: m.tourDesc,
      editor: { kind: 'custom', component: h.tour },
    }),

    // ---------------- Editing ----------------
    row({
      section: 'editing',
      group: m.groupGrid,
      path: 'editing.defaultSnapDiv',
      title: m.defaultSnap,
      description: m.defaultSnapDesc,
      keywords: m.defaultSnapKeywords,
      editor: choice(
        SNAP_DIVS.map((d) => ({ value: d, label: { raw: `1/${String(d)}` } })),
        'select',
      ),
    }),
    row({
      section: 'editing',
      group: m.groupGrid,
      path: 'editing.defaultXGrid',
      title: m.xGrid,
      description: m.xGridDesc,
      editor: { kind: 'number', min: 1, max: 200, step: 1, precision: 0, integer: true },
    }),
    row({
      section: 'editing',
      group: m.groupGrid,
      path: 'editing.snapPlayheadOnPause',
      title: m.snapPlayhead,
      description: m.snapPlayheadDesc,
      keywords: m.snapPlayheadKeywords,
      editor: bool,
    }),
    row({
      section: 'editing',
      group: m.groupTimeline,
      path: 'editing.scrollSensitivity',
      title: m.scrollSensitivity,
      description: m.scrollSensitivityDesc,
      keywords: m.scrollSensitivityKeywords,
      editor: { kind: 'slider', min: 0.05, max: 20, step: 0.01, log: true, format: 'times' },
    }),
    row({
      section: 'editing',
      group: m.groupTimeline,
      path: 'editing.playheadPosition',
      title: m.playheadPosition,
      description: m.playheadPositionDesc,
      editor: { kind: 'slider', min: 0, max: 0.9, step: 0.01, format: 'percent' },
    }),
    row({
      section: 'editing',
      group: m.groupTimeline,
      path: 'editing.jumpToChangeOnUndo',
      title: m.jumpOnUndo,
      description: m.jumpOnUndoDesc,
      editor: bool,
    }),
    row({
      section: 'editing',
      group: m.groupPaste,
      path: 'editing.pasteMode',
      title: m.pasteMode,
      description: m.pasteModeDesc,
      keywords: m.pasteKeywords,
      editor: choice([
        { value: 'ghost', label: m.pasteGhost },
        { value: 'immediate', label: m.pasteImmediate },
      ]),
    }),
    row({
      section: 'editing',
      group: m.groupPaste,
      path: 'editing.pasteAnchor',
      title: m.pasteAnchor,
      description: m.pasteAnchorDesc,
      keywords: m.pasteKeywords,
      editor: choice([
        { value: 'mouse', label: m.pasteAnchorMouse },
        { value: 'playhead', label: m.pasteAnchorPlayhead },
      ]),
    }),
    row({
      section: 'editing',
      group: m.groupFields,
      path: 'editing.beatSyntax',
      title: m.beatSyntax,
      description: m.beatSyntaxDesc,
      keywords: m.beatSyntaxKeywords,
      editor: choice([
        { value: 'default', label: m.beatSyntaxDefault },
        { value: 'rpe', label: m.beatSyntaxRpe },
      ]),
    }),
    row({
      section: 'editing',
      group: m.groupFields,
      path: 'editing.beatDisplay',
      title: m.beatDisplay,
      description: m.beatDisplayDesc,
      editor: choice([
        { value: 'colon', label: { raw: '12:3/4' } },
        { value: 'plus', label: { raw: '12 + 3/4' } },
        { value: 'bar', label: m.beatDisplayBar },
      ]),
    }),
    row({
      section: 'editing',
      group: m.groupFields,
      path: 'editing.units',
      title: m.units,
      description: m.unitsDesc,
      keywords: m.unitsKeywords,
      level: 'full',
      editor: choice([
        { value: 'friendly', label: m.unitsFriendly },
        { value: 'rpe', label: m.unitsRpe },
      ]),
    }),
    row({
      section: 'editing',
      group: m.groupFields,
      path: 'editing.autoKeyDefault',
      title: m.autoKey,
      description: m.autoKeyDesc,
      keywords: m.autoKeyKeywords,
      editor: bool,
    }),
    row({
      section: 'editing',
      group: m.groupNoteDefaults,
      path: 'editing.noteDefaults',
      field: ['above'],
      title: m.noteAbove,
      description: m.noteAboveDesc,
      editor: choice([
        { value: true, label: m.noteAboveYes },
        { value: false, label: m.noteAboveNo },
      ]),
    }),
    row({
      section: 'editing',
      group: m.groupNoteDefaults,
      path: 'editing.noteDefaults',
      field: ['fake'],
      title: m.noteFake,
      description: m.noteFakeDesc,
      editor: bool,
    }),
    row({
      section: 'editing',
      group: m.groupNoteDefaults,
      path: 'editing.noteDefaults',
      field: ['speed'],
      title: m.noteSpeed,
      description: m.noteSpeedDesc,
      level: 'full',
      editor: { kind: 'number', min: -1e6, max: 1e6, step: 0.1, precision: 3, unit: m.unitTimes },
    }),
    row({
      section: 'editing',
      group: m.groupNoteDefaults,
      path: 'editing.noteDefaults',
      field: ['yOffset'],
      title: m.noteYOffset,
      description: m.noteYOffsetDesc,
      level: 'full',
      editor: { kind: 'number', min: -1e6, max: 1e6, step: 1, precision: 2 },
    }),
    row({
      section: 'editing',
      group: m.groupNoteDefaults,
      path: 'editing.noteDefaults',
      field: ['size'],
      title: m.noteSize,
      description: m.noteSizeDesc,
      level: 'full',
      editor: { kind: 'number', min: 0, max: 1000, step: 0.05, precision: 3, unit: m.unitTimes },
    }),
    row({
      section: 'editing',
      group: m.groupNoteDefaults,
      path: 'editing.noteDefaults',
      field: ['alpha'],
      title: m.noteAlpha,
      description: m.noteAlphaDesc,
      level: 'full',
      editor: { kind: 'number', min: 0, max: 255, step: 1, precision: 0, integer: true },
    }),
    row({
      section: 'editing',
      group: m.groupNoteDefaults,
      path: 'editing.noteDefaults',
      field: ['visibleTime'],
      title: m.noteVisibleTime,
      description: m.noteVisibleTimeDesc,
      level: 'full',
      editor: {
        kind: 'number',
        min: 0,
        max: 1e6,
        step: 0.1,
        precision: 3,
        unit: m.unitSeconds,
        nullable: { offLabel: m.noteVisibleTimeOff, onLabel: m.noteVisibleTimeOn, onValue: 1 },
      },
    }),

    // ---------------- Audio ----------------
    row({
      section: 'audio',
      group: m.groupVolumes,
      path: 'audio.volumes',
      field: ['master'],
      title: m.volumeMaster,
      keywords: m.volumeKeywords,
      editor: volume,
    }),
    row({
      section: 'audio',
      group: m.groupVolumes,
      path: 'audio.volumes',
      field: ['music'],
      title: m.volumeMusic,
      keywords: m.volumeKeywords,
      editor: volume,
    }),
    row({
      section: 'audio',
      group: m.groupVolumes,
      path: 'audio.volumes',
      field: ['hitsounds'],
      title: m.volumeHitsounds,
      keywords: m.volumeKeywords,
      editor: volume,
    }),
    row({
      section: 'audio',
      group: m.groupVolumes,
      path: 'audio.volumes',
      field: ['metronome'],
      title: m.volumeMetronome,
      keywords: m.volumeKeywords,
      editor: volume,
    }),
    row({
      section: 'audio',
      group: m.groupLatency,
      path: 'audio.latency',
      field: ['calibrationMs'],
      title: m.latency,
      description: m.latencyDesc,
      keywords: m.latencyKeywords,
      editor: ms,
      extra: h.calibrate,
    }),
    row({
      section: 'audio',
      group: m.groupLatency,
      path: 'audio.latency',
      field: ['hitsoundDelayMs', 'tapFlick'],
      title: m.hitsoundDelayTapFlick,
      description: m.hitsoundDelayDesc,
      keywords: m.latencyKeywords,
      editor: ms,
    }),
    row({
      section: 'audio',
      group: m.groupLatency,
      path: 'audio.latency',
      field: ['hitsoundDelayMs', 'drag'],
      title: m.hitsoundDelayDrag,
      description: m.hitsoundDelayDesc,
      keywords: m.latencyKeywords,
      editor: ms,
    }),
    row({
      section: 'audio',
      group: m.groupPlayback,
      path: 'audio.spaceStops',
      title: m.spaceStops,
      description: m.spaceStopsDesc,
      keywords: m.spaceStopsKeywords,
      editor: choice([
        { value: 'stay', label: m.spaceStopsStay },
        { value: 'return', label: m.spaceStopsReturn },
      ]),
    }),
    row({
      section: 'audio',
      group: m.groupPlayback,
      path: 'audio.preservePitch',
      title: m.preservePitch,
      description: m.preservePitchDesc,
      keywords: m.preservePitchKeywords,
      editor: bool,
    }),

    // ---------------- Preview & skins ----------------
    row({
      section: 'preview',
      path: 'preview.defaultMode',
      title: m.previewMode,
      description: m.previewModeDesc,
      editor: choice([
        { value: 'edit', label: m.previewModeEdit },
        { value: 'clean', label: m.previewModeClean },
      ]),
    }),
    row({
      section: 'preview',
      path: 'preview.renderScale',
      title: m.renderScale,
      description: m.renderScaleDesc,
      keywords: m.renderScaleKeywords,
      level: 'full',
      editor: choice([
        { value: 'auto', label: m.renderScaleAuto },
        { value: 1, label: { key: m.timesValue, params: { value: 1 } } },
        { value: 1.5, label: { key: m.timesValue, params: { value: 1.5 } } },
        { value: 2, label: { key: m.timesValue, params: { value: 2 } } },
      ]),
    }),
    row({
      section: 'preview',
      path: 'preview.fpsCap',
      title: m.fpsCap,
      description: m.fpsCapDesc,
      keywords: m.fpsCapKeywords,
      level: 'full',
      editor: choice([
        { value: 0, label: m.fpsCapNone },
        { value: 30, label: { raw: '30' } },
        { value: 60, label: { raw: '60' } },
        { value: 120, label: { raw: '120' } },
      ]),
    }),
    row({
      section: 'preview',
      group: m.groupPhira,
      path: 'preview.player',
      field: ['noteScale'],
      title: m.noteScale,
      description: m.noteScaleDesc,
      keywords: m.phiraKeywords,
      editor: { kind: 'number', min: 0.1, max: 10, step: 0.05, precision: 2, unit: m.unitTimes },
    }),
    row({
      section: 'preview',
      group: m.groupPhira,
      path: 'preview.player',
      field: ['doubleHint'],
      title: m.doubleHint,
      description: m.doubleHintDesc,
      keywords: m.phiraKeywords,
      editor: bool,
    }),
    row({
      section: 'preview',
      group: m.groupPhira,
      path: 'preview.player',
      field: ['particles'],
      title: m.particles,
      description: m.particlesDesc,
      keywords: m.phiraKeywords,
      editor: bool,
    }),
    row({
      section: 'preview',
      group: m.groupPhira,
      path: 'preview.player',
      field: ['aspectOverride'],
      title: m.aspect,
      description: m.aspectDesc,
      keywords: m.phiraKeywords,
      level: 'full',
      editor: {
        kind: 'number',
        min: 0.1,
        max: 10,
        step: 0.01,
        precision: 3,
        nullable: { offLabel: m.aspectOff, onLabel: m.aspectOn, onValue: 16 / 9 },
      },
    }),
    row({
      section: 'preview',
      group: m.groupPhira,
      path: 'preview.player',
      field: ['aggressiveCull'],
      title: m.aggressiveCull,
      description: m.aggressiveCullDesc,
      keywords: m.phiraKeywords,
      level: 'full',
      editor: bool,
    }),
    row({
      section: 'preview',
      group: m.groupPhira,
      path: 'preview.player',
      field: ['strictAutoplayHitColor'],
      title: m.strictHitColor,
      description: m.strictHitColorDesc,
      keywords: m.phiraKeywords,
      level: 'full',
      editor: bool,
    }),
    row({
      section: 'preview',
      group: m.groupPhira,
      path: 'preview.player',
      field: ['msaa'],
      title: m.msaa,
      description: m.msaaDesc,
      keywords: m.phiraKeywords,
      level: 'full',
      editor: bool,
    }),
    row({
      section: 'preview',
      group: m.groupPhira,
      path: 'preview.player',
      field: ['apFcIndicator'],
      title: m.apFcIndicator,
      description: m.apFcIndicatorDesc,
      keywords: m.phiraKeywords,
      level: 'full',
      editor: bool,
    }),

    // ---------------- Keyboard ----------------
    row({
      section: 'keyboard',
      path: 'keyboard.preset',
      title: m.preset,
      description: m.presetDesc,
      keywords: m.presetKeywords,
      editor: choice([
        { value: 'default', label: m.presetDefault, description: m.presetDefaultDesc },
        { value: 'rpe', label: m.presetRpe, description: m.presetRpeDesc },
      ]),
      write: h.writePreset,
    }),
    row({
      section: 'keyboard',
      path: 'keyboard.altFree',
      title: m.altFree,
      description: m.altFreeDesc,
      keywords: m.altFreeKeywords,
      editor: bool,
      write: h.writeAltFree,
    }),

    // ---------------- Checker ----------------
    row({
      section: 'checker',
      group: m.groupThresholds,
      path: 'checker.thresholds',
      field: ['xMax'],
      title: m.thresholdXMax,
      description: m.thresholdXMaxDesc,
      keywords: m.thresholdKeywords,
      editor: { kind: 'number', min: 0, max: 1e5, step: 10, precision: 0, unit: m.unitPx },
    }),
    row({
      section: 'checker',
      group: m.groupThresholds,
      path: 'checker.thresholds',
      field: ['collisionPx'],
      title: m.thresholdCollision,
      description: m.thresholdCollisionDesc,
      keywords: m.thresholdKeywords,
      editor: { kind: 'number', min: 0, max: 1e5, step: 5, precision: 0, unit: m.unitPx },
    }),
    row({
      section: 'checker',
      group: m.groupThresholds,
      path: 'checker.thresholds',
      field: ['readTimeSec'],
      title: m.thresholdReadTime,
      description: m.thresholdReadTimeDesc,
      keywords: m.thresholdKeywords,
      editor: { kind: 'number', min: 0, max: 60, step: 10, precision: 0, unit: m.unitMs, scale: 1000 },
    }),
    row({
      section: 'checker',
      group: m.groupThresholds,
      path: 'checker.thresholds',
      field: ['dragWindowSec'],
      title: m.thresholdDragWindow,
      description: m.thresholdDragWindowDesc,
      keywords: m.thresholdKeywords,
      editor: { kind: 'number', min: 0, max: 10, step: 5, precision: 0, unit: m.unitMs, scale: 1000 },
    }),
    row({
      section: 'checker',
      group: m.groupThresholds,
      path: 'checker.thresholds',
      field: ['comboWindowSec'],
      title: m.thresholdComboWindow,
      description: m.thresholdComboWindowDesc,
      keywords: m.thresholdKeywords,
      editor: { kind: 'number', min: 0, max: 10, step: 10, precision: 0, unit: m.unitMs, scale: 1000 },
    }),
    row({
      section: 'checker',
      group: m.groupThresholds,
      path: 'checker.thresholds',
      field: ['nearSimultaneousSec'],
      title: m.thresholdNearSimultaneous,
      description: m.thresholdNearSimultaneousDesc,
      keywords: m.thresholdKeywords,
      level: 'full',
      editor: { kind: 'number', min: 0, max: 1, step: 1, precision: 1, unit: m.unitMs, scale: 1000 },
    }),
  ];
}

/** Blocks (custom sections) in page order. */
export const BLOCKS: readonly BlockItem[] = [
  { kind: 'block', id: 'skins', section: 'preview', title: m.skinsTitle, keywords: m.skinsKeywords, level: 'guided' },
  {
    kind: 'block',
    id: 'keymap',
    section: 'keyboard',
    title: m.keymapTitle,
    keywords: m.keymapKeywords,
    level: 'guided',
  },
  {
    kind: 'block',
    id: 'checkerRules',
    section: 'checker',
    title: m.rulesTitle,
    keywords: m.rulesKeywords,
    level: 'guided',
  },
  {
    kind: 'block',
    id: 'storage',
    section: 'storage',
    title: m.storageTitle,
    keywords: m.storageKeywords,
    level: 'guided',
  },
  { kind: 'block', id: 'about', section: 'about', title: m.aboutTitle, keywords: m.aboutKeywords, level: 'guided' },
];

// ---------------------------------------------------------------------------------------------------------------
// Module rows
// ---------------------------------------------------------------------------------------------------------------

/** A module's SettingDef as a page row (default editor by kind, or its custom component). */
export function moduleRow(def: SettingDef, moduleId: string): RowItem {
  const section = pageSectionOfPath(def.path) ?? 'features';
  let editor: RowEditor;
  switch (def.kind) {
    case 'boolean':
      editor = bool;
      break;
    case 'number':
      editor = {
        kind: 'number',
        min: def.min ?? -Number.MAX_VALUE,
        max: def.max ?? Number.MAX_VALUE,
        ...(def.step === undefined ? {} : { step: def.step }),
        // The module's unit text is its own (SettingDef.unit is plain text).
        ...(def.unit === undefined ? {} : { unit: { raw: def.unit } }),
      };
      break;
    case 'enum':
      editor = choice((def.options ?? []).map((o) => ({ value: o.value, label: o.label })));
      break;
    case 'string':
      editor = { kind: 'text' };
      break;
    case 'custom':
      editor =
        def.component === undefined
          ? { kind: 'text' }
          : { kind: 'custom', component: def.component as ComponentType<{ readonly services: AppServices }> };
      break;
  }
  return {
    kind: 'row',
    id: def.path,
    section,
    path: def.path,
    field: [],
    title: def.title,
    ...(def.description === undefined ? {} : { description: def.description }),
    level: def.level,
    editor,
    moduleId,
  };
}

/**
 * Every page item in order: built-in rows (replaced by module rows of the same path), module rows of other
 * built-in paths after the section's own rows, ext rows in "Panels and tools", blocks at their section's end.
 */
export function pageItems(
  builtins: readonly RowItem[],
  modules: ReadonlyArray<{ readonly id: string; readonly settings?: readonly SettingDef[] }>,
): PageItem[] {
  const contributed: RowItem[] = [];
  const seen = new Set<string>();
  for (const mod of modules) {
    for (const def of mod.settings ?? []) {
      if (seen.has(def.path)) continue;
      seen.add(def.path);
      contributed.push(moduleRow(def, mod.id));
    }
  }
  const replaced = new Set(contributed.map((r) => r.path));
  const out: PageItem[] = [];
  for (const section of PAGE_SECTIONS) {
    const own = builtins.filter((r) => r.section === section.id);
    // A module row takes the place of the first built-in row of its path; the others of that path are dropped.
    const placed = new Set<string>();
    for (const r of own) {
      if (!replaced.has(r.path)) {
        out.push(r);
        continue;
      }
      if (placed.has(r.path)) continue;
      placed.add(r.path);
      const mod = contributed.find((c) => c.path === r.path);
      if (mod !== undefined)
        out.push({ ...mod, section: r.section, ...(r.group === undefined ? {} : { group: r.group }) });
    }
    for (const c of contributed) {
      if (c.section === section.id && !placed.has(c.path)) out.push(c);
    }
    for (const b of BLOCKS) if (b.section === section.id) out.push(b);
  }
  return out;
}

// ---------------------------------------------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------------------------------------------

export type Translate = (key: MessageKey | string, params?: Readonly<Record<string, string | number>>) => string;

export function optionText(t: Translate, label: OptionLabel): string {
  if (typeof label === 'string') return t(label);
  return 'raw' in label ? label.raw : t(label.key, label.params);
}

/** Translated texts a row is found by (title, description, keywords, group, option labels, path). */
export function rowSearchTexts(t: Translate, r: RowItem): string[] {
  const texts = [t(r.title), r.path, r.id];
  if (r.description !== undefined) texts.push(t(r.description));
  if (r.keywords !== undefined) texts.push(t(r.keywords));
  if (r.group !== undefined) texts.push(t(r.group));
  if (r.editor.kind === 'choice') for (const o of r.editor.options) texts.push(optionText(t, o.label));
  return texts;
}

export function blockSearchTexts(t: Translate, b: BlockItem): string[] {
  return [t(b.title), t(b.keywords)];
}

export function itemMatches(t: Translate, item: PageItem, terms: readonly string[]): boolean {
  return matchesTerms(terms, item.kind === 'row' ? rowSearchTexts(t, item) : blockSearchTexts(t, item));
}

/** The page section an anchor (section id, row id or setting path) belongs to. */
export function sectionOfAnchor(items: readonly PageItem[], anchor: string): PageSectionId | null {
  if (isPageSectionId(anchor)) return anchor;
  const hit = items.find((i) => i.id === anchor || (i.kind === 'row' && i.path === anchor));
  if (hit !== undefined) return hit.section;
  const bySection = pageSectionOfPath(anchor);
  return bySection;
}
